import { useState, useEffect } from 'react';
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  Trophy,
  Activity,
  AlertCircle,
  BarChart3,
  RefreshCw,
  Loader2,
  ChevronRight,
  Eye,
  Scale,
  Zap,
  Clock,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';

const InsightCard = ({ insight }) => {
  const iconMap = {
    TrendingUp: TrendingUp,
    TrendingDown: TrendingDown,
    AlertCircle: AlertCircle,
    Trophy: Trophy,
    BarChart3: BarChart3
  };
  
  const Icon = iconMap[insight.icon] || Zap;
  
  const typeStyles = {
    positive: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    action: 'bg-blue-50 border-blue-200 text-blue-700',
    info: 'bg-slate-50 border-slate-200 text-slate-700'
  };
  
  return (
    <div className={cn(
      "flex items-start gap-3 p-3 rounded-lg border",
      typeStyles[insight.type] || typeStyles.info
    )}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <p className="text-sm">{insight.text}</p>
    </div>
  );
};

const LeaderboardItem = ({ item, maxScore }) => {
  const percentage = (item.engagement_score / maxScore) * 100;
  
  return (
    <div className="flex items-center gap-3 py-2">
      <div className={cn(
        "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold",
        item.rank === 1 ? "bg-yellow-100 text-yellow-700" :
        item.rank === 2 ? "bg-slate-100 text-slate-700" :
        item.rank === 3 ? "bg-amber-100 text-amber-700" :
        "bg-muted text-muted-foreground"
      )}>
        {item.rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-medium truncate">{item.name}</p>
          <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
            {item.engagement_score} pts
          </span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all",
              item.rank === 1 ? "bg-yellow-500" :
              item.rank <= 3 ? "bg-primary" : "bg-primary/60"
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" /> {item.views}
          </span>
          <span className="flex items-center gap-1">
            <Scale className="w-3 h-3" /> {item.comparisons}
          </span>
        </div>
      </div>
    </div>
  );
};

const TrendItem = ({ item, isRising }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-2 min-w-0">
      {isRising ? (
        <ArrowUp className="w-4 h-4 text-green-500 flex-shrink-0" />
      ) : (
        <ArrowDown className="w-4 h-4 text-red-500 flex-shrink-0" />
      )}
      <span className="text-sm truncate">{item.name}</span>
    </div>
    <Badge 
      variant="secondary" 
      className={cn(
        "text-xs ml-2",
        isRising ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      )}
    >
      {isRising ? '+' : ''}{item.change_percent}%
    </Badge>
  </div>
);

const ActivityItem = ({ event }) => {
  const timeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const diff = Math.floor((now - then) / 1000);
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };
  
  const typeColors = {
    wizard_complete: 'bg-blue-500',
    platform_view: 'bg-green-500',
    comparison_view: 'bg-purple-500',
    saved_result: 'bg-amber-500'
  };
  
  return (
    <div className="flex items-start gap-3 py-2">
      <div className={cn(
        "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
        typeColors[event.type] || 'bg-muted'
      )} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">{event.description}</p>
        <p className="text-xs text-muted-foreground">{timeAgo(event.timestamp)}</p>
      </div>
    </div>
  );
};

export const AdvancedAnalytics = () => {
  const [briefing, setBriefing] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [trends, setTrends] = useState(null);
  const [activityStream, setActivityStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAllData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [briefingRes, leaderboardRes, trendsRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/ai-briefing`),
        fetch(`${API_URL}/api/admin/analytics/leaderboard`),
        fetch(`${API_URL}/api/admin/analytics/trends`),
        fetch(`${API_URL}/api/admin/analytics/activity-stream`)
      ]);
      
      if (briefingRes.ok) setBriefing(await briefingRes.json());
      if (leaderboardRes.ok) setLeaderboard(await leaderboardRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      if (activityRes.ok) setActivityStream(await activityRes.json());
    } catch (err) {
      console.error('Failed to fetch advanced analytics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // Refresh activity stream every 30 seconds
    const interval = setInterval(() => fetchAllData(true), 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const maxScore = leaderboard?.leaderboard?.[0]?.engagement_score || 1;

  return (
    <div className="space-y-6">
      {/* AI Briefing Card */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-primary/10">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Daily Briefing</CardTitle>
                <CardDescription className="text-xs">
                  AI-powered insights for {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
                </CardDescription>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => fetchAllData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="text-center p-2 bg-background rounded-lg">
              <p className="text-2xl font-bold text-foreground">{briefing?.summary?.today_events || 0}</p>
              <p className="text-xs text-muted-foreground">Today's Events</p>
            </div>
            <div className="text-center p-2 bg-background rounded-lg">
              <p className="text-2xl font-bold text-foreground">{briefing?.summary?.week_completions || 0}</p>
              <p className="text-xs text-muted-foreground">Week Completions</p>
            </div>
            <div className="text-center p-2 bg-background rounded-lg">
              <p className="text-2xl font-bold text-foreground">{briefing?.summary?.week_views || 0}</p>
              <p className="text-xs text-muted-foreground">Week Views</p>
            </div>
            <div className="text-center p-2 bg-background rounded-lg">
              <div className="flex items-center justify-center gap-1">
                {briefing?.summary?.today_events > briefing?.summary?.yesterday_events ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : briefing?.summary?.today_events < briefing?.summary?.yesterday_events ? (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                ) : (
                  <Minus className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">
                  vs yesterday
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Trend</p>
            </div>
          </div>
          
          {/* Insights */}
          {briefing?.insights?.length > 0 && (
            <div className="space-y-2">
              {briefing.insights.map((insight, i) => (
                <InsightCard key={i} insight={insight} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Three Column Layout */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Leaderboard */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-500" />
              Platform Leaderboard
            </CardTitle>
            <CardDescription className="text-xs">
              Top performers by engagement score (Last 30 days)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 divide-y">
              {leaderboard?.leaderboard?.slice(0, 7).map(item => (
                <LeaderboardItem key={item.platform_id} item={item} maxScore={maxScore} />
              ))}
            </div>
            {(!leaderboard?.leaderboard || leaderboard.leaderboard.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No engagement data yet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Trends */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Platform Trends
            </CardTitle>
            <CardDescription className="text-xs">
              Week-over-week momentum changes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trends?.rising?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-green-600 mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Rising
                </p>
                <div className="space-y-1">
                  {trends.rising.map(item => (
                    <TrendItem key={item.platform_id} item={item} isRising />
                  ))}
                </div>
              </div>
            )}
            {trends?.falling?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" /> Falling
                </p>
                <div className="space-y-1">
                  {trends.falling.map(item => (
                    <TrendItem key={item.platform_id} item={item} isRising={false} />
                  ))}
                </div>
              </div>
            )}
            {(!trends?.rising?.length && !trends?.falling?.length) && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Not enough data for trend analysis
              </p>
            )}
          </CardContent>
        </Card>

        {/* Activity Stream */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Live Activity
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
            </CardTitle>
            <CardDescription className="text-xs">
              Real-time user activity feed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {activityStream?.events?.map((event, i) => (
                <ActivityItem key={i} event={event} />
              ))}
            </div>
            {(!activityStream?.events || activityStream.events.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No recent activity
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
